import { onScopeDispose } from "vue";
import { useRouter } from "vue-router";

import type { Router } from "vue-router";

import { useAzureStore } from "@/store/azure.store";
import { useLicenceStore } from "@/modules/Usage/store/licence.store";
import {
  TITAN_REMOTE_COMPANY_SCOPE_CHANGE_EVENT,
  isEmbedded,
} from "@/remote/contract";
// Host -> titan only; the host owns the company (see companyScope.ts).
import {
  PARTNER,
  currentScope,
  isCompanyScope,
  isSameOrg,
  publishedScope,
  routeScope,
  systemAccountOrg,
  type OrgRef,
} from "@/remote/companyScope";
import {
  navigateToCustomerOrganisation,
  navigateToMspOrganisation,
} from "@/utils/navigation/organisationNavigation";

/** Never fatal: this runs before the navigation, and a stale cache beats not switching at all. */
const clearOrgScopedCaches = (): void => {
  try {
    useAzureStore().clearAzureCache();
    useLicenceStore().clearLicenceCache();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn(
      "[titan] could not clear the org-scoped caches before switching company — switching " +
        "anyway, so some panels may show the previous company's data until they refetch.",
      error,
    );
  }
};

// Breaker on re-navigating to the same company. Rate, not count: nobody re-picks one company four
// times in three seconds, and a bounce loop does nothing else.
const LOOP_ATTEMPTS = 3;
const LOOP_WINDOW_MS = 3000;

let recentTargets: { at: number; ref: OrgRef }[] = [];

const sameRef = (left: OrgRef | null, right: OrgRef | null): boolean =>
  left !== null &&
  right !== null &&
  left.type === right.type &&
  left.uuid === right.uuid;

/** Records this attempt and reports whether the same company is being re-applied too fast. */
const isLooping = (target: OrgRef): boolean => {
  const now = Date.now();
  recentTargets = recentTargets.filter(
    (entry) => now - entry.at < LOOP_WINDOW_MS && sameRef(entry.ref, target),
  );
  recentTargets.push({ at: now, ref: target });
  return recentTargets.length > LOOP_ATTEMPTS;
};

/** DEV-only. "Titan stayed put" has four causes; each exit below says which one it took. */
const trace = (message: string, detail?: unknown): void => {
  // eslint-disable-next-line runtime-env/no-buildtime-env
  if (!import.meta.env.DEV) return;
  // eslint-disable-next-line no-console
  console.log(`[titan:scope] ${message}`, detail ?? "");
};

/** Enough to identify a scope in a console line, whatever shape the host sent. */
const describeScope = (published: unknown): string => {
  if (!isCompanyScope(published)) return "the company it published";
  return `${published.type} ${published.organisationUuid}`;
};

const applyCompanyScope = async (
  published: unknown,
  router: Router,
): Promise<void> => {
  if (!isCompanyScope(published)) {
    trace("ignored — the host published no usable scope", published);
    return;
  }

  // Not a failure: `/system/<uuid>` IS that company's page. See `systemAccountOrg`.
  if (systemAccountOrg() === published.organisationUuid) {
    trace(
      "staying put — the host's company is this System-tier account's OWN org",
      published.organisationUuid,
    );
    return;
  }

  // The check below reads the route, which is `/` until the initial navigation resolves.
  await router.isReady();

  // Both, because the store is persisted: a fresh mount reports an earlier visit's company while
  // its own boot landed elsewhere.
  if (
    isSameOrg(currentScope(), published) &&
    isSameOrg(routeScope(router), published)
  ) {
    trace(
      "already there — store and route both agree",
      published.organisationUuid,
    );
    return;
  }

  const target: OrgRef = {
    type: published.type,
    uuid: published.organisationUuid,
  };

  if (isLooping(target)) {
    // eslint-disable-next-line no-console
    console.warn(
      `[titan] refusing to navigate to ${target.type} ${target.uuid} again — ${LOOP_ATTEMPTS}+ ` +
        `attempts in ${LOOP_WINDOW_MS}ms means something is navigating titan straight back. ` +
        "Leaving it where it is; see useCompanyScopeSync.",
    );
    return;
  }

  trace(`navigating to ${published.type} ${published.organisationUuid}`, {
    from: router.currentRoute.value.fullPath,
  });
  clearOrgScopedCaches();

  const organisation = {
    id: published.organisationUuid,
    name: published.name || "",
  };
  await (published.type === PARTNER
    ? navigateToMspOrganisation({ router }, organisation)
    : navigateToCustomerOrganisation({ router }, organisation));
};

/** TEST ONLY — the breaker's window is module state and outlives a test case. */
export const __resetCompanyScopeSyncForTests = (): void => {
  recentTargets = [];
};

/** Follows the host's switcher while titan is on screen; the boot landing is `companyLanding.ts`.
 * No `router.go(0)` — the page is the host's, so a reload would take the shell with it. */
export function useCompanyScopeSync(): void {
  if (!isEmbedded()) return;

  const router = useRouter();

  // Serialised: two scopes in flight would both pass the already-current check.
  let queue = Promise.resolve();
  const apply = (published: unknown) => {
    queue = queue
      .then(() => applyCompanyScope(published, router))
      // Caught so the next scope still runs, reported so a failed switch is not invisible.
      .catch((error: unknown) => {
        // eslint-disable-next-line no-console
        console.error(
          "[titan] could not follow the host to " +
            `${describeScope(published)} — staying on the current company.`,
          error,
        );
      });
  };

  // The global is current, so a company selected before this mounted is not lost.
  trace("mounted; global holds", publishedScope());
  apply(publishedScope());

  const onScopeChange = (event: Event) =>
    apply((event as CustomEvent<unknown>).detail);

  document.addEventListener(
    TITAN_REMOTE_COMPANY_SCOPE_CHANGE_EVENT,
    onScopeChange,
  );
  onScopeDispose(() =>
    document.removeEventListener(
      TITAN_REMOTE_COMPANY_SCOPE_CHANGE_EVENT,
      onScopeChange,
    ),
  );
}

export default useCompanyScopeSync;
